// Interfaces for Feedback Messages
export interface FeedbackMessage {
  id: string;
  timestamp: Date;
  type: 'info' | 'validation' | 'transfer' | 'close';
  message: string;
  details?: string;
  success?: boolean;
}

// Interfaces for Modal Props
export interface HVADynamicConversationModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  agentCode: string;
  agentName: string;
  agentImage?: string;
  onError?: (error: Error) => void;
  customerId?: string;
  email?: string;
  name?: string;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
}

// Interfaces for Products and Orders
export interface Product {
  id: string;
  nombre: string;
  categoria: string;
  descripcion: string;
  precio: number;
  stock: number;
  especificaciones: Record<string, string | number>;
}

export interface OrderProduct {
  id: string;
  cantidad: number;
  precio_unitario: number;
}

export interface OrderShipping {
  direccion: string;
  metodo: string;
  numero_seguimiento: string;
  estado_envio: string;
  fecha_entrega?: string;
  fecha_entrega_estimada?: string;
}

export interface OrderClient {
  id: string;
  nombre: string;
  email: string;
}

export interface Order {
  id: string;
  cliente: OrderClient;
  fecha_pedido: string;
  estado: string;
  productos: OrderProduct[];
  envio: OrderShipping;
  total: number;
}

export interface StoreData {
  productos: Product[];
  pedidos: Order[];
}

// Static Data
export const CATALOG_DATA: StoreData = {
  productos: [
    {
      id: 'FON-001',
      nombre: 'Grifo monomando lavabo',
      categoria: 'Fontanería',
      descripcion: 'Grifo monomando para lavabo con cartucho cerámico',
      precio: 49.99,
      stock: 25,
      especificaciones: {
        material: 'Latón cromado',
        altura: '15.5 cm',
        garantia: '5 años',
        normativa: 'UNE-EN 817'
      }
    },
    {
      id: 'FON-002',
      nombre: 'Tubo PVC evacuación',
      categoria: 'Fontanería',
      descripcion: 'Tubo de PVC para evacuación de aguas',
      precio: 3.99,
      stock: 200,
      especificaciones: {
        diametro: '40 mm',
        longitud: '2 m',
        color: 'Gris',
        presion: 'PN-6'
      }
    },
    {
      id: 'FON-003',
      nombre: 'Cisterna empotrada doble descarga',
      categoria: 'Fontanería',
      descripcion: 'Cisterna empotrada con pulsador de doble descarga',
      precio: 89.99,
      stock: 15,
      especificaciones: {
        capacidad: '6/3 L',
        dimensiones: '50x12x80 cm',
        material: 'ABS reforzado'
      }
    },
    {
      id: 'FON-004',
      nombre: 'Fregadero acero inoxidable',
      categoria: 'Fontanería',
      descripcion: 'Fregadero de dos senos con escurridor',
      precio: 129.99,
      stock: 10,
      especificaciones: {
        material: 'Acero inoxidable 18/10',
        dimensiones: '90x50 cm',
        profundidad: '20 cm'
      }
    },
    {
      id: 'FON-005',
      nombre: 'Kit ducha termostática',
      categoria: 'Fontanería',
      descripcion: 'Conjunto ducha con grifo termostático',
      precio: 159.99,
      stock: 8,
      especificaciones: {
        altura: '120 cm',
        rociador: '20 cm diámetro',
        funciones: '3 tipos de chorro'
      }
    },
    {
      id: 'PIN-001',
      nombre: 'Pintura plástica mate interior',
      categoria: 'Pintura',
      descripcion: 'Pintura lavable para paredes y techos',
      precio: 19.99,
      stock: 100,
      especificaciones: {
        capacidad: '15 L',
        rendimiento: '8-10 m²/L',
        secado: '1 hora',
        color: 'Blanco'
      }
    },
    {
      id: 'PIN-002',
      nombre: 'Esmalte sintético brillante',
      categoria: 'Pintura',
      descripcion: 'Esmalte multiusos para interior y exterior',
      precio: 12.99,
      stock: 50,
      especificaciones: {
        capacidad: '750 ml',
        rendimiento: '12 m²/L',
        secado: '6 horas',
        colores: 'Negro/Blanco/Gris'
      }
    },
    {
      id: 'PIN-003',
      nombre: 'Pintura antioxidante',
      categoria: 'Pintura',
      descripcion: 'Imprimación antioxidante para metales',
      precio: 15.99,
      stock: 30,
      especificaciones: {
        capacidad: '750 ml',
        rendimiento: '10 m²/L',
        secado: '2 horas'
      }
    },
    {
      id: 'PIN-004',
      nombre: 'Pintura impermeabilizante terrazas',
      categoria: 'Pintura',
      descripcion: 'Pintura para impermeabilización de terrazas',
      precio: 45.99,
      stock: 25,
      especificaciones: {
        capacidad: '20 L',
        rendimiento: '1.5 L/m²',
        resistencia: 'UV y agua'
      }
    },
    {
      id: 'PIN-005',
      nombre: 'Masilla reparadora',
      categoria: 'Pintura',
      descripcion: 'Masilla para reparación de grietas',
      precio: 8.99,
      stock: 60,
      especificaciones: {
        peso: '1 kg',
        secado: '2 horas',
        grosor: 'hasta 5 cm'
      }
    },
    {
      id: 'FER-001',
      nombre: 'Taladro percutor',
      categoria: 'Ferretería',
      descripcion: 'Taladro percutor profesional 800W',
      precio: 89.99,
      stock: 15,
      especificaciones: {
        potencia: '800W',
        mandril: '13 mm',
        velocidad: '0-3000 rpm'
      }
    },
    {
      id: 'FER-002',
      nombre: 'Juego destornilladores',
      categoria: 'Ferretería',
      descripcion: 'Set 6 destornilladores precisión',
      precio: 15.99,
      stock: 40,
      especificaciones: {
        piezas: '6',
        tipos: 'Plano y Phillips',
        material: 'Cromo-vanadio'
      }
    },
    {
      id: 'FER-003',
      nombre: 'Sierra circular',
      categoria: 'Ferretería',
      descripcion: 'Sierra circular manual 1200W',
      precio: 129.99,
      stock: 10,
      especificaciones: {
        potencia: '1200W',
        disco: '185 mm',
        corte: '45°'
      }
    },
    {
      id: 'FER-004',
      nombre: 'Caja herramientas completa',
      categoria: 'Ferretería',
      descripcion: 'Maletín con 130 herramientas',
      precio: 99.99,
      stock: 12,
      especificaciones: {
        piezas: '130',
        material: 'Cromo-vanadio',
        maletin: 'ABS reforzado'
      }
    },
    {
      id: 'FER-005',
      nombre: 'Escalera multiusos',
      categoria: 'Ferretería',
      descripcion: 'Escalera aluminio 4x4 peldaños',
      precio: 89.99,
      stock: 8,
      especificaciones: {
        altura: '4.7 m',
        peso: '12 kg',
        capacidad: '150 kg'
      }
    },
    {
      id: 'ELE-001',
      nombre: 'Cable eléctrico H07V-K',
      categoria: 'Electricidad',
      descripcion: 'Cable unipolar flexible',
      precio: 0.99,
      stock: 1000,
      especificaciones: {
        seccion: '2.5 mm²',
        color: 'Azul',
        tension: '450/750V'
      }
    },
    {
      id: 'ELE-002',
      nombre: 'Cuadro eléctrico 12 elementos',
      categoria: 'Electricidad',
      descripcion: 'Cuadro distribución superficie',
      precio: 29.99,
      stock: 20,
      especificaciones: {
        elementos: '12',
        proteccion: 'IP40',
        material: 'ABS'
      }
    },
    {
      id: 'ELE-003',
      nombre: 'Diferencial 2P 40A',
      categoria: 'Electricidad',
      descripcion: 'Interruptor diferencial 30mA',
      precio: 39.99,
      stock: 25,
      especificaciones: {
        intensidad: '40A',
        sensibilidad: '30mA',
        polos: '2P'
      }
    },
    {
      id: 'ELE-004',
      nombre: 'Panel LED 60x60',
      categoria: 'Electricidad',
      descripcion: 'Panel LED para techo 40W',
      precio: 49.99,
      stock: 30,
      especificaciones: {
        potencia: '40W',
        luminosidad: '4000 lm',
        color: '4000K'
      }
    },
    {
      id: 'ELE-005',
      nombre: 'Enchufe superficie estanco',
      categoria: 'Electricidad',
      descripcion: 'Base enchufe Schuko IP55',
      precio: 8.99,
      stock: 50,
      especificaciones: {
        proteccion: 'IP55',
        corriente: '16A',
        material: 'PVC'
      }
    }
  ],
  pedidos: [
    {
      id: 'PED-001',
      cliente: {
        id: 'CLI-123',
        nombre: 'Juan García Pérez',
        email: 'juan.garcia@email.com'
      },
      fecha_pedido: '2024-01-20T10:30:00',
      estado: 'Entregado',
      productos: [
        {
          id: 'FON-001',
          cantidad: 1,
          precio_unitario: 49.99
        },
        {
          id: 'FON-002',
          cantidad: 5,
          precio_unitario: 3.99
        }
      ],
      envio: {
        direccion: 'Calle Mayor 23, 28001 Madrid',
        metodo: 'Estándar',
        numero_seguimiento: 'EN123456789ES',
        estado_envio: 'Entregado',
        fecha_entrega: '2024-01-23T15:45:00'
      },
      total: 69.94
    },
    {
      id: 'PED-002',
      cliente: {
        id: 'CLI-456',
        nombre: 'María López Sánchez',
        email: 'maria.lopez@email.com'
      },
      fecha_pedido: '2024-01-25T16:20:00',
      estado: 'En preparación',
      productos: [
        {
          id: 'PIN-001',
          cantidad: 2,
          precio_unitario: 19.99
        },
        {
          id: 'PIN-003',
          cantidad: 3,
          precio_unitario: 15.99
        }
      ],
      envio: {
        direccion: 'Avenida Libertad 45, 41001 Sevilla',
        metodo: 'Express',
        numero_seguimiento: 'EX987654321ES',
        estado_envio: 'En preparación',
        fecha_entrega_estimada: '2024-01-27T14:00:00'
      },
      total: 87.95
    },
    {
      id: 'PED-003',
      cliente: {
        id: 'CLI-789',
        nombre: 'Antonio Martínez Ruiz',
        email: 'antonio.martinez@email.com'
      },
      fecha_pedido: '2024-01-26T09:15:00',
      estado: 'Enviado',
      productos: [
        {
          id: 'ELE-001',
          cantidad: 50,
          precio_unitario: 0.99
        },
        {
          id: 'ELE-002',
          cantidad: 10,
          precio_unitario: 29.99
        }
      ],
      envio: {
        direccion: 'Plaza España 12, 08001 Barcelona',
        metodo: 'Estándar',
        numero_seguimiento: 'ST567891234ES',
        estado_envio: 'En tránsito',
        fecha_entrega_estimada: '2024-01-29T12:00:00'
      },
      total: 349.4
    }
  ]
};
